/* eslint-disable react/jsx-props-no-spreading */
import Grid from '@material-ui/core/Grid';
import RootRef from '@material-ui/core/RootRef';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import 'core-js/features/object/from-entries';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import validate from 'validate.js';
import Consent from '../components/Consent/Consent';
import Footer from '../components/Footer/Footer';
import { FormPhoneInput, FormSelect, FormTextField } from '../components/Form';
import Terms from '../components/Terms/Terms';
import { MainHeadingSmaller, SubtleText } from '../components/typography';
import { submitMetric } from '../helpers/metrics';
import PropTypes, { consentPropType, intlPropType } from '../helpers/propTypes';
import { useCloseConfirmation } from '../hooks/useCloseConfirmation';
import { useGlobalScope } from '../hooks/useGlobalState';
import useListEntries from '../hooks/useListEntries';
import usePageAria from '../hooks/usePageAria';
import { userMetrics } from '../userMetrics';
import messages from './Messages';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    scrollBehavior: 'smooth',
    position: 'relative',

    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },

  rootLoading: {
    opacity: 0.5,
    pointerEvents: 'none',
  },

  promptText: {
    margin: theme.spacing(2, 0),
    width: '100%',
    maxWidth: 600,
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },

  form: {
    width: '60%',
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      width: '80%',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },

  consentContainer: {
    marginTop: theme.spacing(2),
  },
}));

validate.options = {
  fullMessages: false,
};

validate.validators.phone = (value, { message }) => {
  const phoneNumber = parsePhoneNumberFromString(value);
  if (
    !phoneNumber ||
    Object.keys(phoneNumber).length === 0 ||
    !phoneNumber.isPossible()
  ) {
    return message;
  }
  return undefined;
};

const typesToComponents = {
  select: FormSelect,
  email: FormTextField,
  phone: FormPhoneInput,
  name: FormTextField,
  text: FormTextField,
  postalCode: FormTextField,
};

const getConstraints = ({ formatMessage, fieldsConfig }) => {
  const constraints = {
    name: {
      length: {
        minimum: 2,
        tooShort: formatMessage(messages.MinimumCharacters, {
          value: '%{count}',
        }),
        maximum: 255,
        tooLong: formatMessage(messages.MaximumCharacters, {
          value: '%{count}',
        }),
      },
    },
    preferredContactWay: {
      presence: {
        allowEmpty: false,
        message: formatMessage(messages.PleaseSelectPreferredContactWay),
      },
    },
    preferredContactHours: {
      presence: {
        allowEmpty: false,
        message: formatMessage(messages.PleaseSelectPreferredContactHours),
      },
    },
    email: {
      email: {
        message: formatMessage(messages.NotValidEmail),
      },
    },
    emailConfirm: {
      equality: {
        attribute: 'email',
        message: formatMessage(messages.NonEqualEmail),
      },
    },
    phone: {
      phone: {
        message: formatMessage(messages.NotValidPhoneNumber),
      },
    },
    phoneConfirm: {
      equality: {
        attribute: 'phone',
        message: formatMessage(messages.NonEqualPhoneNumber),
      },
    },
    postalCode: {
      presence: {
        allowEmpty: false,
        message: formatMessage(messages.NotValidPostalCode),
      },
    },
    consented: {
      inclusion: {
        within: [true],
        message: formatMessage(messages.YouMustConsent),
      },
    },
  };
  return Object.fromEntries(
    fieldsConfig
      .map(({ name, confirm }) => (confirm ? [name, `${name}Confirm`] : [name]))
      .reduce((acc, keys) => [...acc, ...keys], ['consented'])
      .map((key) => [key, constraints[key]]),
  );
};

const FormField = injectIntl(
  ({
    intl: { formatMessage },
    field,
    fieldsConfig,
    fieldValues,
    onChange,
    onBlur,
    onFocus,
    additionalProps,
    rootRef,
    required,
  }) => {
    const { type, name } = field;
    const { value, errors } = fieldValues;
    const InputComponent = typesToComponents[type];
    const theme = useTheme();

    let size = 12;
    if (useMediaQuery(theme.breakpoints.up('sm'))) {
      const hasTime = fieldsConfig.some(
        (f) => f.name === 'preferredContactHours',
      );
      const hasWay = fieldsConfig.some((f) => f.name === 'preferredContactWay');
      const hasPhoneConfirm = fieldsConfig.some(
        (f) => f.name === 'phone' && f.confirm === true,
      );
      const size12 =
        ((hasTime && !hasWay) || (!hasTime && hasWay)) && hasPhoneConfirm;

      if (!size12) {
        if (name === 'phone')
          size = (hasTime && !hasWay) || (!hasTime && hasWay) ? 7 : 12;
        else if (name === 'preferredContactWay') size = !hasTime ? 5 : 6;
        else if (name === 'preferredContactHours') size = !hasWay ? 5 : 6;
      }
    }

    let optionsSelection;
    if (field.name === 'preferredContactWay') {
      const mappings = {
        Email: messages.emailOptionPlaceholder,
        'Phone call': messages.phoneOptionPlaceholder,
        SMS: messages.smsOptionPlaceholder,
      };

      const mapped = field.options.map((obj) => {
        const key = mappings[obj.name];
        if (key) {
          return {
            ...obj,
            name: formatMessage(key),
          };
        }
        return obj;
      });
      optionsSelection = mapped;
    } else {
      optionsSelection = field.options;
    }

    return (
      <RootRef rootRef={rootRef}>
        <Grid key={name} item xs={size}>
          <InputComponent
            additionalProps={additionalProps}
            data-testid={`field-${name}`}
            error={errors && errors[0]}
            label={messages[name] ? formatMessage(messages[name]) : name}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            options={optionsSelection}
            placeholder={
              messages[`${name}Placeholder`]
                ? formatMessage(messages[`${name}Placeholder`])
                : undefined
            }
            required={required}
            type={type}
            value={value}
          />
        </Grid>
      </RootRef>
    );
  },
);

function ContactDetails({
  intl: { formatMessage },
  fields: fieldsConfig,
  consent,
  pageName,
  nextPageName,
  prevPageName,
  navigateModal,
  api: { submitContactDetails },
  contentId,
  ...props
}) {
  const fields = useMemo(() => {
    const processedFields = [];
    fieldsConfig.forEach((fieldData) => {
      const field = {
        ...fieldData,
        options: fieldData.options
          ? fieldData.options.map((value) => {
              const name = messages[value]
                ? formatMessage(messages[value])
                : value;
              return { name, value };
            })
          : [],
      };
      processedFields.push(field);
      if (field.confirm) {
        processedFields.push({
          name: `${field.name}Confirm`,
          type: field.type,
        });
      }
    });
    return processedFields;
  }, [fieldsConfig]);

  useCloseConfirmation();

  useEffect(() => {
    userMetrics.track('Contact info screen loaded');
  }, []);

  const useGlobal = useGlobalScope(pageName);

  const [fieldValues, setFieldValues, setNestedFieldValue] = useGlobal(
    'fieldValues',
    () => {
      const values = {};
      fields.forEach(({ name, defaultValue }) => {
        values[name] = { value: defaultValue || '', errors: [] };
      });
      return values;
    },
  );
  const [consented, setConsented] = useGlobal('consented', false);
  const [consentAnswers, setConsentAnswers] = useGlobal('consentAnswers', {});
  const [consentError, setConsentError] = useGlobal('consentError', '');
  const [subPage, setSubPage] = useGlobal('subPage');
  const [scrollToConsent, setScrollToConsent] = useState(false);

  const rootRef = useRef();
  const consentRef = useRef();
  const fieldRefs = useListEntries(
    fields,
    () => React.createRef(),
    (i) => fields[i].name,
  );

  const aria = usePageAria({ ref: rootRef });

  const [loading, setLoading] = useState(false);
  const [startValidating, setStartValidating] = useState(false);

  const classes = useStyles();

  const closeTerms = () => {
    setSubPage(null);
    setScrollToConsent(true);
  };

  const scrollToElement = (element) => {
    const { offsetTop } = element;
    rootRef.current.scrollTop = offsetTop;
  };

  const validateForm = () =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      const attributes = Object.keys(fieldValues).reduce(
        (acc, name) => ({
          ...acc,
          [name]: fieldValues[name].value,
        }),
        {},
      );
      const constraints = getConstraints({ formatMessage, fieldsConfig });

      const errors = validate(attributes, constraints);

      if (!errors && consented) {
        resolve();
      }

      const newValues = {};
      Object.entries(fieldValues).forEach(([name, properties]) => {
        newValues[name] = {
          ...properties,
          errors: (errors || {})[name] || [],
        };
      });

      setFieldValues(newValues);
      setConsentError(!consented ? formatMessage(messages.YouMustConsent) : '');
      reject(errors);
    });

  useEffect(() => {
    if (startValidating) validateForm().catch(() => {});
  }, [consented, ...Object.values(fieldValues).map(({ value }) => value)]);

  useEffect(() => {
    if (scrollToConsent && consentRef.current) {
      scrollToElement(consentRef.current);
      setScrollToConsent(false);
    }
  }, [scrollToConsent, consentRef]);

  if (subPage) {
    return <Terms termsAndAgreement={subPage} close={closeTerms} />;
  }

  const handleOnModified = async () => {
    submitMetric('contactDetailsModified');
  };

  const handleFocus = (name, type) => {
    const _type = type === 'select' ? type : 'text field';
    userMetrics.track(`Contact info ${_type} focus ${name}`);
  };

  const scrollToFirstInvalidField = (errors) => {
    try {
      if (!errors && !consented && consentRef?.current) {
        scrollToElement(consentRef.current);
      } else {
        const field = fieldRefs[Object.keys(errors)[0]].current;
        scrollToElement(field);
        field.querySelector('input').focus();
      }
    } catch (_) {
      // Cannot scroll, not a big deal. Let user continue working...
    }
  };

  const handleCompleteClick = async () => {
    try {
      await validateForm();
    } catch (errors) {
      scrollToFirstInvalidField(errors);
      setStartValidating(true);
      return;
    }

    const contactDetails = {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,

      ...Object.fromEntries(
        Object.entries(fieldValues)
          .filter(([key]) => !key.match(/Confirm$/))
          .map(([name, { value }]) => [name, value]),
      ),
    };

    setLoading(true);

    const { eligible } = await submitContactDetails({
      contactDetails,
      consentAnswers,
    });

    setLoading(false);
    if (eligible) {
      navigateModal({ forward: true });
    } else {
      setConsentError(formatMessage(messages.YouMustConsent));
      setStartValidating(true);
    }
  };

  const handleBackClick = () => {
    navigateModal({ forward: false });
    setSubPage(null);
  };

  const handleConsent = ({ valid, answers }) => {
    setConsented(valid);
    setConsentAnswers(answers);
    handleOnModified();
  };

  const handleFieldChange = (e) => {
    setNestedFieldValue(`${e.name}.value`, e.value);
    if (startValidating) validateForm().catch(() => {});
  };

  return (
    <>
      <div
        className={classNames(classes.root, { [classes.rootLoading]: loading })}
        {...aria}
      >
        <MainHeadingSmaller className={classes.promptText}>
          <FormattedMessage
            defaultMessage="Please enter your contact details and preferred time of contact so that the study medical team can reach you to discuss study participation."
            id="ContactDetails.prompt"
          />
        </MainHeadingSmaller>
        <SubtleText className={classes.promptText}>
          <FormattedMessage
            defaultMessage="Submitting your contact details does not mean you have to join the study. That is your decision to make if you are fully eligible for the study."
            id="ContactDetails.PromptNotice"
          />
        </SubtleText>
        <div className={classes.form}>
          <Grid container direction="row" spacing={3} wrap="wrap">
            {fields.map((field) => (
              <FormField
                key={field.name}
                data-testid={`field-${field.name}`}
                additionalProps={props}
                field={field}
                fieldsConfig={fieldsConfig}
                fieldValues={fieldValues[field.name]}
                onBlur={handleOnModified}
                onChange={handleFieldChange}
                onFocus={() => handleFocus(field.name, field.type)}
                required
                rootRef={fieldRefs[field.name]}
              />
            ))}
          </Grid>
          <Grid
            alignItems="center"
            className={classes.consentContainer}
            container
            spacing={3}
          >
            <Grid item>
              <SubtleText>
                <FormattedMessage
                  defaultMessage="Your personal information and your answers to the study questions are only used for the purposes of determining if the clinical study is suitable for you. You can at any time withdraw your consent and have your personal data removed."
                  id="ContactDetails.privacyDisclaimer"
                />
              </SubtleText>
            </Grid>

            <Consent
              ref={consentRef}
              consent={consent}
              error={consentError}
              handleConsent={handleConsent}
              openTerms={setSubPage}
            />
          </Grid>
        </div>
      </div>
      <Footer
        formIsComplete
        liveRegionId={contentId}
        loading={loading}
        nextPageName={nextPageName}
        onBackClick={handleBackClick}
        onContinueClick={handleCompleteClick}
        prevPageName={prevPageName}
      />
    </>
  );
}

ContactDetails.propTypes = {
  api: PropTypes.shape({
    submitContactDetails: PropTypes.func.isRequired,
  }).isRequired,
  consent: consentPropType.isRequired,
  contentId: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    }),
  ).isRequired,
  intl: intlPropType.isRequired,
  navigateModal: PropTypes.func.isRequired,
  nextPageName: PropTypes.string,
  pageName: PropTypes.string.isRequired,
  prevPageName: PropTypes.string,
};

ContactDetails.defaultProps = {
  nextPageName: undefined,
  prevPageName: undefined,
};

export default injectIntl(ContactDetails);
